import { useState } from "react";
import {
  ArrowLeftRight,
  BellRing,
  Calculator,
  ChevronDown,
  Layers,
  Loader2,
  MessageSquarePlus,
  Send,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Textarea } from "@/components/ui/textarea";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { QuotePdfButton } from "@/components/admin/QuotePdfButton";
import { StageBlock } from "@/components/admin/LeadStageBlock";
import { QuoteWorkingsByRef } from "@/components/admin/QuoteWorkings";
import { adminApi, messageFrom } from "@/lib/api";
import { formatDate, formatDateTime, money, relativeTime } from "@/lib/format";

/**
 * Everything the team can see and do on one lead.
 *
 * Built as a handful of small blocks rather than one long panel: each block
 * owns only what it needs, and the blocks that hold their own state are keyed
 * on the quote reference so opening a different lead starts them clean instead
 * of carrying the last lead's half-typed note across.
 */

/* ---- the figure the customer was given ---- */
const MoneyBlock = ({ lead, onCompare }) => (
  <div className="mt-5 rounded-lg border border-[color:var(--tt-teal-line)] bg-[color:var(--tt-teal-mist)] px-4 py-3">
    <p className="text-[10px] font-bold uppercase tracking-[0.12em] text-[color:var(--tt-teal-deep)]">
      {lead.pricing_type === "monthly" ? "Monthly fee" : "Quoted"}
    </p>
    <p
      data-testid="lead-sheet-amount"
      className="mt-1 font-amount text-2xl font-bold text-[color:var(--tt-teal-deep)]"
    >
      {money(lead.quote_total)}
      {lead.pricing_type === "monthly" ? (
        <span className="text-sm font-normal">/month</span>
      ) : null}
    </p>
    <p className="mt-1 text-xs text-[color:var(--tt-teal-deep)]">
      {lead.headline} · <span className="font-amount">{lead.quote_ref}</span>
    </p>
    {lead.pricing_type === "monthly" && lead.first_invoice_total ? (
      <p className="mt-1 font-amount text-xs text-[color:var(--tt-teal-deep)]">
        {money(lead.first_invoice_total)} due on the first invoice
      </p>
    ) : null}
    <QuotePdfButton quoteRef={lead.quote_ref} className="mt-3 w-full bg-white" />
    {lead.is_repeat ? (
      <Button
        type="button"
        variant="outline"
        data-testid="lead-sheet-compare-button"
        onClick={() => onCompare?.(lead)}
        className="mt-2 h-10 w-full bg-white text-[13px] font-semibold"
      >
        <ArrowLeftRight className="mr-2 h-4 w-4 shrink-0" aria-hidden="true" />
        Compare all {lead.quote_count} of their quotes
      </Button>
    ) : null}
  </div>
);

/* ---- staff-only derivation behind that figure ---- */
const WorkingsBlock = ({ quoteRef }) => {
  const [open, setOpen] = useState(false);

  return (
    <Collapsible open={open} onOpenChange={setOpen} className="mt-3">
      <CollapsibleTrigger asChild>
        <Button
          type="button"
          variant="outline"
          data-testid="lead-sheet-workings-toggle"
          className="h-10 w-full justify-between bg-white text-[13px] font-semibold"
        >
          <span className="flex items-center gap-2">
            <Calculator className="h-4 w-4 shrink-0 text-primary" aria-hidden="true" />
            How we got to this figure
          </span>
          <ChevronDown
            className={`h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-200 ${
              open ? "rotate-180" : ""
            }`}
            aria-hidden="true"
          />
        </Button>
      </CollapsibleTrigger>
      <CollapsibleContent className="mt-3">
        {open ? <QuoteWorkingsByRef quoteRef={quoteRef} /> : null}
      </CollapsibleContent>
    </Collapsible>
  );
};

/* ---- how to reach them ---- */
const ContactDetails = ({ lead }) => (
  <dl className="mt-4 space-y-1.5 text-sm">
    <div className="flex items-baseline justify-between gap-3">
      <dt className="text-muted-foreground">Email</dt>
      <dd>
        <a href={`mailto:${lead.email}`} className="tt-link" data-testid="lead-sheet-email">
          {lead.email}
        </a>
      </dd>
    </div>
    <div className="flex items-baseline justify-between gap-3">
      <dt className="text-muted-foreground">Phone</dt>
      <dd>
        <a href={`tel:${lead.phone_display}`} className="tt-link">
          {lead.phone_display}
        </a>
      </dd>
    </div>
    {lead.best_time ? (
      <div className="flex items-baseline justify-between gap-3">
        <dt className="text-muted-foreground">Best time to call</dt>
        <dd className="font-medium">{lead.best_time}</dd>
      </div>
    ) : null}
    {lead.next_step_label ? (
      <div className="flex items-baseline justify-between gap-3">
        <dt className="text-muted-foreground">They asked for</dt>
        <dd className="font-medium">{lead.next_step_label}</dd>
      </div>
    ) : null}
    <div className="flex items-baseline justify-between gap-3">
      <dt className="text-muted-foreground">Last activity</dt>
      <dd className="font-medium">{relativeTime(lead.last_activity_at || lead.created_at)}</dd>
    </div>
  </dl>
);

const QuietNotice = ({ days }) => (
  <p
    data-testid="lead-sheet-gone-quiet"
    className="mt-4 flex items-start gap-2 rounded-lg border px-3 py-2.5 text-[13px] leading-relaxed"
    style={{
      borderColor: "var(--tt-warn-border)",
      backgroundColor: "var(--tt-warn-bg)",
      color: "var(--tt-warn-text)",
    }}
  >
    <BellRing className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
    Nothing has happened on this lead for {days} days. Worth a nudge.
  </p>
);

/* ---- what the team has written down ---- */
const NotesBlock = ({ lead, busy, patch }) => {
  const [note, setNote] = useState("");
  const notes = [...(lead.notes || [])].reverse();

  const add = async () => {
    const saved = await patch({ note }, "note");
    if (saved) setNote("");
  };

  return (
    <div className="mt-5">
      <h4 className="tt-eyebrow">Notes</h4>
      <div className="mt-2">
        <Textarea
          data-testid="lead-sheet-note-input"
          value={note}
          onChange={(event) => setNote(event.target.value)}
          placeholder="What happened on this lead? e.g. Called — they're comparing two firms, will decide next week."
          rows={3}
          className="resize-none bg-white text-sm"
        />
        <Button
          type="button"
          onClick={add}
          disabled={!note.trim() || busy === "note"}
          data-testid="lead-sheet-add-note-button"
          className="mt-2 h-10 w-full"
        >
          {busy === "note" ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
          ) : (
            <MessageSquarePlus className="mr-2 h-4 w-4" aria-hidden="true" />
          )}
          Add note
        </Button>
      </div>

      {notes.length ? (
        <ol data-testid="lead-sheet-notes" className="mt-4 space-y-3">
          {notes.map((entry, index) => (
            <li
              key={`${entry.at}-${index}`}
              className="rounded-lg border border-border bg-[color:var(--tt-surface-2)] px-3 py-2.5"
            >
              <p className="text-[13px] leading-relaxed text-foreground">{entry.text}</p>
              <p className="mt-1.5 text-[11px] text-muted-foreground">
                {entry.by} · {formatDateTime(entry.at)}
              </p>
            </li>
          ))}
        </ol>
      ) : (
        <p className="mt-3 text-[13px] text-muted-foreground">
          No notes yet. Anything you add here is visible to the whole team.
        </p>
      )}
    </div>
  );
};

/* ---- the quote email itself ---- */
const EmailBlock = ({ lead, emailConfigured, busy, onResend }) => {
  const delivery = lead.email_delivery || {};
  let status = "Quote emails are switched off until a Resend API key is configured.";
  if (delivery.sent) {
    status = `Sent to ${delivery.to || lead.email}${
      delivery.at ? ` on ${formatDateTime(delivery.at)}` : ""
    }.`;
  } else if (emailConfigured) {
    status = "Not sent yet.";
  }

  return (
    <div className="mt-5 rounded-lg border border-border p-4">
      <h4 className="tt-eyebrow">Quote email</h4>
      <p className="mt-2 text-[13px] leading-relaxed text-muted-foreground">{status}</p>
      <Button
        type="button"
        variant="outline"
        onClick={onResend}
        disabled={!emailConfigured || busy === "email"}
        data-testid="lead-sheet-resend-email-button"
        className="mt-3 h-10 w-full bg-white"
      >
        {busy === "email" ? (
          <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
        ) : (
          <Send className="mr-2 h-4 w-4" aria-hidden="true" />
        )}
        {delivery.sent ? "Send the PDF again" : "Email the PDF now"}
      </Button>
    </div>
  );
};

export const LeadSheet = ({ lead, stages, reasons, emailConfigured, onClose, onSaved,
                            onOutcomeNeeded, onCompare }) => {
  const [busy, setBusy] = useState(null);

  if (!lead) return null;

  const patch = async (body, label) => {
    setBusy(label);
    try {
      const data = await adminApi.updateLead(lead.quote_ref, body);
      onSaved(data.lead);
      toast.success(`Saved — ${data.changed.join(", ")}.`);
      return data.lead;
    } catch (error) {
      toast.error(messageFrom(error));
      return null;
    } finally {
      setBusy(null);
    }
  };

  const resend = async () => {
    setBusy("email");
    try {
      await adminApi.resendQuoteEmail(lead.quote_ref);
      toast.success(`Quote PDF emailed to ${lead.email}.`);
    } catch (error) {
      toast.error(messageFrom(error));
    } finally {
      setBusy(null);
    }
  };

  return (
    <Sheet open onOpenChange={(open) => !open && onClose()}>
      <SheetContent
        data-testid="lead-detail-sheet"
        className="w-full overflow-y-auto bg-white sm:max-w-lg"
      >
        <SheetHeader className="text-left">
          <SheetTitle className="pr-8 text-lg">{lead.full_name}</SheetTitle>
          <SheetDescription>
            {lead.company} · quoted {formatDate(lead.created_at)}
          </SheetDescription>
          {lead.is_repeat ? (
            <p
              data-testid="lead-sheet-sequence"
              className="mt-2 flex items-center gap-1.5 text-xs font-semibold text-[color:var(--tt-teal-deep)]"
            >
              <Layers className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
              Quote {lead.quote_number} of {lead.quote_count} from this customer
            </p>
          ) : null}
        </SheetHeader>

        <MoneyBlock lead={lead} onCompare={onCompare} />
        <WorkingsBlock key={`workings-${lead.quote_ref}`} quoteRef={lead.quote_ref} />
        <ContactDetails lead={lead} />
        {lead.gone_quiet ? <QuietNotice days={lead.days_since_activity} /> : null}
        <StageBlock
          lead={lead}
          stages={stages}
          reasons={reasons}
          busy={busy}
          patch={patch}
          onOutcomeNeeded={onOutcomeNeeded}
        />
        <NotesBlock key={`notes-${lead.quote_ref}`} lead={lead} busy={busy} patch={patch} />
        <EmailBlock
          lead={lead}
          emailConfigured={emailConfigured}
          busy={busy}
          onResend={resend}
        />
      </SheetContent>
    </Sheet>
  );
};
